/**
 * What colours a view's rows, and on what grounds.
 *
 * Two questions per decoration, which is how the backend stores them: a
 * *decorator* says where the colour goes -- down the left edge or across the
 * whole row -- and a *value provider* says which colour a given row gets. Both
 * are chosen when the decoration is added, because a decoration with no
 * provider is a row in this list that colours nothing and says nothing about
 * why; and both stay changeable afterwards, because either provider drives
 * either decorator.
 *
 * Every edit rewrites the whole `value_provider_conf` and waits for the answer.
 * The backend edits those conditions behind the client's back -- see
 * `decoration-mutations.ts` -- so what comes back is the truth about what is
 * stored, and drawing the sent version first would show conditions the server
 * has already dropped.
 */
import { Palette, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type {
  ConditionalColor,
  ConditionalColorConf,
  ConditionalColorFilter,
  DecorationUpdate,
  DecoratorTypeName,
  Field,
  SelectColorConf,
  ValueProviderTypeName,
  ViewDecoration,
} from '@/api/types'
import { useFieldContext } from '../fields/ui/context'
import type { FieldContext } from '../fields/behavior/behavior'
import type { RowsView } from '../views/field-order'
import { SELECT_COLOR_NAMES } from '../fields/select-options'
import { ColourPicker } from '../fields/ui/forms/controls'
import { canFilterField, filterFor, filterTypesFor } from '../filters/inputs'
import { buildFilterTree, type TreeNode } from '../rows/rowMatch'
import { Button } from '@/shared/ui/button'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { ConditionsNode, type ConditionId } from '../table/condition-tree'
import {
  defaultConditionalConf,
  defaultSelectConf,
  nextColor,
  patchColor,
  withCondition,
  withColor,
  withGroup,
  withoutColor,
  withoutCondition,
  withoutGroup,
} from './conf'
import { useViewDecorations } from './decoration-mutations'

/** The two places a colour can go, in the order they are offered. */
const DECORATORS = ['left_border_color', 'background_color'] as const

/** The two ways a row's colour is worked out. */
const PROVIDERS = ['single_select_color', 'conditional_color'] as const

/**
 * An id for something nothing issues one for.
 *
 * A colour, a condition and a group all live inside one JSON column, so no
 * table hands out their ids and the client invents them. Unique across tabs
 * and sessions, because two people editing one decoration would otherwise
 * write two different things under one name.
 */
const newId = (): string => crypto.randomUUID()

export function DecorationsMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: RowsView
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const context = useFieldContext(fields[0]?.workspace_id)
  const { add, change, remove } = useViewDecorations(tableId, view.id)

  const decorations = view.decorations ?? []

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Palette className="size-4 opacity-70" />
          {decorations.length === 0
            ? t('view.decoration')
            : t('view.decorationCount', { count: decorations.length })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[40rem] p-2">
        {decorations.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('view.decorationNone')}
          </p>
        ) : (
          <ul aria-label={t('view.decoration')} className="flex flex-col gap-2">
            {decorations.map((decoration) => (
              <DecorationRow
                key={decoration.id}
                decoration={decoration}
                fields={fields}
                context={context}
                onChange={(changes) => {
                  change(decoration.id, changes)
                }}
                onRemove={() => {
                  remove(decoration.id)
                }}
              />
            ))}
          </ul>
        )}

        <div className="mt-2 flex flex-wrap gap-2 border-t pt-2">
          {PROVIDERS.map((provider) => (
            <Button
              key={provider}
              variant="outline"
              size="sm"
              onClick={() => {
                // Both questions answered at once, and the decorator is the
                // quieter of the two: a left border says a row is picked out
                // without recolouring the text somebody is reading.
                add(
                  provider === 'single_select_color'
                    ? {
                        type: 'left_border_color',
                        value_provider_type: provider,
                        value_provider_conf: defaultSelectConf(fields),
                      }
                    : {
                        type: 'left_border_color',
                        value_provider_type: provider,
                        value_provider_conf: defaultConditionalConf(
                          newId(),
                          SELECT_COLOR_NAMES[0]!,
                        ),
                      },
                )
              }}
            >
              {t(`view.decorationAdd_${provider}`)}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function DecorationRow({
  decoration,
  fields,
  context,
  onChange,
  onRemove,
}: {
  decoration: ViewDecoration
  fields: readonly Field[]
  context: FieldContext
  onChange: (changes: DecorationUpdate) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const provider = decoration.value_provider_type as ValueProviderTypeName

  return (
    <li className="rounded border p-2">
      <div className="flex items-center gap-2">
        <NativeSelect
          size="sm"
          className="w-40"
          aria-label={t('view.decorationWhere')}
          value={decoration.type ?? ''}
          onChange={(event) => {
            onChange({ type: event.target.value as DecoratorTypeName })
          }}
        >
          {DECORATORS.map((name) => (
            <NativeSelectOption key={name} value={name}>
              {t(`view.decorator_${name}`)}
            </NativeSelectOption>
          ))}
        </NativeSelect>

        <NativeSelect
          size="sm"
          className="w-48"
          aria-label={t('view.decorationBy')}
          value={provider}
          onChange={(event) => {
            // The configuration goes with the provider: what a select column's
            // configuration says means nothing to the conditional one, so
            // switching starts the new one at its own default rather than
            // sending the old shape under a new name.
            const next = event.target.value as ValueProviderTypeName
            onChange(
              next === 'single_select_color'
                ? {
                    value_provider_type: next,
                    value_provider_conf: defaultSelectConf(fields),
                  }
                : {
                    value_provider_type: next,
                    value_provider_conf: defaultConditionalConf(
                      newId(),
                      SELECT_COLOR_NAMES[0]!,
                    ),
                  },
            )
          }}
        >
          {PROVIDERS.map((name) => (
            <NativeSelectOption key={name} value={name}>
              {t(`view.provider_${name}`)}
            </NativeSelectOption>
          ))}
        </NativeSelect>

        <button
          type="button"
          aria-label={t('view.decorationRemove')}
          onClick={onRemove}
          className="ml-auto rounded p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="size-3.5" />
        </button>
      </div>

      <div className="mt-2">
        {provider === 'single_select_color' ? (
          <SelectColorEditor
            conf={(decoration.value_provider_conf ?? {}) as SelectColorConf}
            fields={fields}
            onChange={(conf) => {
              onChange({ value_provider_type: provider, value_provider_conf: conf })
            }}
          />
        ) : provider === 'conditional_color' ? (
          <ConditionalColorEditor
            conf={
              (decoration.value_provider_conf ?? { colors: [] }) as ConditionalColorConf
            }
            fields={fields}
            context={context}
            onChange={(conf) => {
              onChange({ value_provider_type: provider, value_provider_conf: conf })
            }}
          />
        ) : (
          // A provider this build does not know: a view configured by another
          // version. Named rather than drawn, so it can still be removed --
          // rendering an editor for a shape nothing here understands would
          // write over a configuration it cannot read.
          <p className="text-xs text-muted-foreground">
            {t('view.providerUnknown', { name: decoration.value_provider_type ?? '' })}
          </p>
        )}
      </div>
    </li>
  )
}

/** A colour taken from the option a single select column holds. */
function SelectColorEditor({
  conf,
  fields,
  onChange,
}: {
  conf: SelectColorConf
  fields: readonly Field[]
  onChange: (conf: SelectColorConf) => void
}) {
  const { t } = useTranslation()
  const selects = fields.filter((field) => field.type === 'single_select')

  if (selects.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t('view.decorationNoSelects')}</p>
    )
  }

  return (
    <NativeSelect
      size="sm"
      className="w-56"
      aria-label={t('view.decorationSelectField')}
      value={conf.field_id ?? ''}
      onChange={(event) => {
        const value = event.target.value
        onChange({ field_id: value === '' ? null : Number(value) })
      }}
    >
      <NativeSelectOption value="">{t('view.decorationNoField')}</NativeSelectOption>
      {selects.map((field) => (
        <NativeSelectOption key={field.id} value={field.id}>
          {field.name}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}

/**
 * A list of colours, each with the conditions a row must meet to get it.
 *
 * The list is an order of precedence rather than a set: the first colour whose
 * conditions a row meets is the one it takes, which is what `decorationColor`
 * does with it and what makes the order worth showing.
 */
function ConditionalColorEditor({
  conf,
  fields,
  context,
  onChange,
}: {
  conf: ConditionalColorConf
  fields: readonly Field[]
  context: FieldContext
  onChange: (conf: ConditionalColorConf) => void
}) {
  const { t } = useTranslation()
  const filterable = fields.filter((field) => canFilterField(field))

  const edit = (
    colorId: string,
    change: (color: ConditionalColor) => ConditionalColor,
  ): void => {
    onChange(patchColor(conf, colorId, change))
  }

  return (
    <div className="flex flex-col gap-2">
      <ol aria-label={t('view.decorationColors')} className="flex flex-col gap-2">
        {conf.colors.map((color, index) => (
          <li key={color.id} className="flex items-start gap-2">
            <ColourPicker
              label={t('view.decorationColor', { position: index + 1 })}
              colour={color.color}
              onChange={(next) => {
                edit(color.id, (held) => ({ ...held, color: next }))
              }}
            />
            <div className="flex-1">
              <ColorConditions
                color={color}
                fields={filterable}
                context={context}
                onChange={(next) => {
                  edit(color.id, () => next)
                }}
              />
            </div>
            <button
              type="button"
              aria-label={t('view.decorationRemoveColor', { position: index + 1 })}
              onClick={() => {
                onChange(withoutColor(conf, color.id))
              }}
              className="mt-1 rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="size-3.5" />
            </button>
          </li>
        ))}
      </ol>

      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onChange(
              withColor(conf, {
                id: newId(),
                color: nextColor(conf),
                filters: [],
                operator: 'AND',
              }),
            )
          }}
        >
          {t('view.decorationAddColor')}
        </Button>
      </div>
    </div>
  )
}

/** One colour's conditions, drawn by the same tree a view's filters are. */
function ColorConditions({
  color,
  fields,
  context,
  onChange,
}: {
  color: ConditionalColor
  fields: readonly Field[]
  context: FieldContext
  onChange: (color: ConditionalColor) => void
}) {
  const { t } = useTranslation()

  // `operator` is this colour's own connective, so a colour is a view-shaped
  // thing as far as the tree is concerned -- exactly as it is to the matcher.
  const tree = buildFilterTree<ConditionalColorFilter>({
    filter_type: color.operator,
    filters: color.filters,
    filter_groups: color.filter_groups ?? [],
  })

  const asGroup = (id: ConditionId | null): string | null =>
    typeof id === 'string' ? id : null

  function addCondition(group: ConditionId | null): void {
    const field = fields[0]
    if (field === undefined) return
    const type = filterTypesFor(field)[0]
    if (type === undefined) return
    onChange(
      withCondition(color, {
        ...filterFor(field, type.type),
        id: newId(),
        group: asGroup(group),
      }),
    )
  }

  if (fields.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('view.filterNoFields')}</p>
  }

  return (
    <div className="flex flex-col gap-1">
      {color.filters.length === 0 && tree.children.length === 0 ? (
        // A colour with no conditions matches every row -- the backend says so
        // -- so this says which it is rather than reading as an empty list that
        // does nothing.
        <p className="px-1 py-0.5 text-xs text-muted-foreground">
          {t('view.decorationEveryRow')}
        </p>
      ) : (
        <ConditionsNode
          node={tree}
          label={t('view.decorationConditions')}
          fields={fields}
          context={context}
          onConnective={(node, next) => {
            const id = node.id === null ? null : asGroup(node.id)
            if (id === null) onChange({ ...color, operator: next })
            else
              onChange(
                withGroup(color, {
                  ...groupOf(node, color),
                  id,
                  filter_type: next,
                }),
              )
          }}
          onAddFilter={addCondition}
          onAddGroup={(parent) => {
            onChange(
              withGroup(color, {
                id: newId(),
                filter_type: 'AND',
                parent_group: asGroup(parent),
              }),
            )
          }}
          onChange={(id, changes) => {
            const held = color.filters.find((condition) => condition.id === id)
            if (held === undefined) return
            onChange(withCondition(color, { ...held, ...changes }))
          }}
          onRemove={(id) => {
            const stored = asGroup(id)
            if (stored !== null) onChange(withoutCondition(color, stored))
          }}
          onRemoveGroup={(id) => {
            const stored = asGroup(id)
            if (stored !== null) onChange(withoutGroup(color, stored))
          }}
        />
      )}

      <div className="flex gap-2">
        <button
          type="button"
          className="rounded px-1 py-0.5 text-xs text-muted-foreground hover:bg-muted"
          onClick={() => {
            addCondition(null)
          }}
        >
          {t('view.filterAdd')}
        </button>
        <button
          type="button"
          className="rounded px-1 py-0.5 text-xs text-muted-foreground hover:bg-muted"
          onClick={() => {
            onChange(
              withGroup(color, { id: newId(), filter_type: 'AND', parent_group: null }),
            )
          }}
        >
          {t('view.filterAddGroup')}
        </button>
      </div>
    </div>
  )
}

/**
 * The stored group a node stands for.
 *
 * The tree carries only what evaluating needs, so changing a group's connective
 * has to find the stored row again to keep its parent -- which the tree knows
 * and does not carry.
 */
function groupOf(
  node: TreeNode<ConditionalColorFilter>,
  color: ConditionalColor,
): { id: string; filter_type: 'AND' | 'OR'; parent_group?: string | null } {
  const held = (color.filter_groups ?? []).find((group) => group.id === node.id)
  return held ?? { id: String(node.id), filter_type: node.conjunction }
}
